/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef } from 'react';
import {
  FlatList,
  FlatListProps,
  Keyboard,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  ScrollViewProps,
} from 'react-native';
import { ListOption } from './list-item';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface KeyboardAwareOptions {
  autoAdjustDisabled?: boolean;
  extractNativeRef?: (ref: ScrollView | null) => ScrollView | null;
  refPropName?: string;
}

export interface KeyboardAwareProps {
  autoAdjustDisabled?: boolean;
  extraSpacing?: number;
}

export function withKeyboardAware<
  TProps extends ScrollViewProps = ScrollViewProps,
>(
  // Any is used here because the type is used in a generic context
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>,
  {
    extractNativeRef = (x) => x,
    refPropName = 'ref',
  }: KeyboardAwareOptions = {},
): React.FC<TProps & KeyboardAwareProps> {
  if (Platform.OS === 'android') {
    return ({
      autoAdjustDisabled = false,
      extraSpacing,
      ...props
    }: TProps & KeyboardAwareProps) => {
      const componentRef = useRef<ScrollView>(null);

      const componentProps = {
        ...props,
        [refPropName]: componentRef,
      };

      return <Component {...componentProps} />;
    };
  }

  return ({
    autoAdjustDisabled = false,
    extraSpacing = 20,
    ...props
  }: TProps & KeyboardAwareProps) => {
    const { bottom } = useSafeAreaInsets();

    const componentRef = useRef<ScrollView>(null);
    const isMountedRef = useRef(false);
    const offsetRef = useRef(0);
    const lastOffsetRef = useRef<number>(null);

    const onScroll = props.onScroll;
    const onScrollCallback = useCallback(
      (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        if (event.nativeEvent.contentOffset) {
          const viewHeight = event.nativeEvent.contentSize.height;
          const screenLayoutHeight = event.nativeEvent.layoutMeasurement.height;

          offsetRef.current =
            viewHeight < screenLayoutHeight
              ? 0
              : event.nativeEvent.contentOffset.y;
        }
        onScroll?.(event);
      },
      [onScroll],
    );

    useEffect(() => {
      const openEvent = Keyboard.addListener('keyboardWillShow', (event) => {
        if (isMountedRef.current && !autoAdjustDisabled) {
          lastOffsetRef.current = offsetRef.current;

          const scrollView = extractNativeRef(componentRef.current);
          if (scrollView) {
            const keyboardHeight = event.endCoordinates.height;
            scrollView.setNativeProps({
              contentInset: { bottom: keyboardHeight + extraSpacing - bottom },
            });
          }
        }
      });

      const closeEvent = Keyboard.addListener('keyboardWillHide', () => {
        if (isMountedRef.current && !autoAdjustDisabled) {
          const scrollView = extractNativeRef(componentRef.current);
          if (scrollView) {
            scrollView.setNativeProps({
              contentInset: { bottom: 0 },
            });

            // Delay until after any blur events
            setTimeout(() => {
              scrollView.scrollTo({
                animated: true,
                y: lastOffsetRef.current ?? offsetRef.current,
              });
            }, 50);
          }
        }
      });

      isMountedRef.current = true;

      return () => {
        isMountedRef.current = false;

        openEvent.remove();
        closeEvent.remove();
      };
    }, [extraSpacing, autoAdjustDisabled, bottom]);

    // keyboard aware onScroll calls component's onScroll
    const { onScroll: _componentOnScroll, ...restProps } = props;
    const componentProps: ScrollViewProps = {
      automaticallyAdjustContentInsets: false,
      onScroll: onScrollCallback,
      onScrollBeginDrag: (e) => {
        // Clear lastOffset if the user manually scrolls to prevent it from
        // animating back to the input
        lastOffsetRef.current = null;
        restProps.onScrollBeginDrag?.(e);
      },
      scrollEventThrottle: 16,
      ...restProps,
      [refPropName]: componentRef,
    };

    return <Component {...componentProps} />;
  };
}

export const KeyboardAwareFlatList: <TListOption = ListOption>(
  props: FlatListProps<TListOption> & KeyboardAwareProps,
) => React.ReactNode | Promise<React.ReactNode> = withKeyboardAware(FlatList, {
  // @ts-ignore
  extractNativeRef: (x) => x?.getNativeScrollRef(),
});
export const KeyboardAwareScrollView = withKeyboardAware(ScrollView);
